import { useSelector } from "react-redux";

import { Permission } from "~/models/dto";
import type { ResourcePermission } from "~/models/permissions";
import { Permitted } from "~/tools/permitted";

import { usePermissions } from "../../../../common/use-permissions";
import { selectPlayground } from "../../config/ConfigSelectors";
import { selectIsComponentPage, selectIsViewPage } from "../../route/RouteSelectors";
import { isViewLockProtected } from "../ViewUtils";

import { isViewQueryValid } from "./ViewPointHelpers";
import { useCurrentInitialView, useCurrentView, useIsCurrentViewDirty } from "./ViewPointHooks";
import { selectIsExploreSubview } from "./ViewPointSelectors";

const useIsCurrentViewLockProtected = () => useCurrentInitialView().transform(isViewLockProtected).getOrElse(true);

// CAN RESET VIEW

export interface CanResetQueryParams {
	isViewDirty: boolean;
	currentViewQuery: string;
}

export const canResetQuery = ({ isViewDirty, currentViewQuery }: CanResetQueryParams): boolean =>
	isViewDirty && (currentViewQuery === "" || isViewQueryValid(currentViewQuery));

export const useCanResetCurrentQuery = (): boolean => {
	const isCurrentViewDirty = useIsCurrentViewDirty();
	const currentView = useCurrentView();

	return Permitted.all({
		isDirty: isCurrentViewDirty,
		view: currentView,
	})
		.transform(({ isDirty, view }) => canResetQuery({ isViewDirty: isDirty, currentViewQuery: view.query }))
		.getOrElse(false);
};

// CAN SAVE VIEW

export interface CanSaveViewParams {
	canReset: boolean;
	viewName: undefined | string;
	isViewPage: boolean;
	isExploreSubview: boolean;
	isLockProtected: boolean;
}

export const canSaveView = ({
	canReset,
	isViewPage,
	isExploreSubview,
	isLockProtected,
}: Omit<CanSaveViewParams, "viewName">): boolean => canReset && isViewPage && !isExploreSubview && !isLockProtected;

export const useCanSaveCurrentView = (): boolean => {
	const permissions = usePermissions();
	const canReset = useCanResetCurrentQuery();
	const currentView = useCurrentView();
	const isViewPage = useSelector(selectIsViewPage);
	const isExploreSubview = useSelector(selectIsExploreSubview);
	const isLockProtected = useIsCurrentViewLockProtected();

	return currentView
		.transform(({ name: viewName }) => {
			if (viewName == null || !permissions?.hasResourcePermission(viewName, Permission.UpdateViews)) {
				return false;
			}

			return canSaveView({ canReset, isViewPage, isExploreSubview, isLockProtected });
		})
		.getOrElse(false);
};

// CAN SAVE VIEW AS

export interface CanSaveViewAsParams {
	isExploreSubview: boolean;
	isComponentPage: boolean;
}

export const canSaveViewAs = ({ isExploreSubview, isComponentPage }: CanSaveViewAsParams): boolean =>
	// don't save as components, but it is fine to save components subview as new view
	!isComponentPage || isExploreSubview;

export const useCanSaveCurrentViewAs = (): boolean => {
	const permissions = usePermissions();
	const isExploreSubview = useSelector(selectIsExploreSubview);
	const isComponentPage = useSelector(selectIsComponentPage);

	if (!permissions?.hasSystemPermission(Permission.CreateViews)) {
		return false;
	}

	return canSaveViewAs({ isExploreSubview, isComponentPage });
};

// CAN EDIT/DELETE VIEW

export interface CanEditOrDeleteViewParams {
	isViewPage: boolean;
	isExploreSubview: boolean;
	isPlayground: boolean;
	isLockProtected: boolean;
}

export const canEditOrDeleteView = ({
	isViewPage,
	isExploreSubview,
	isPlayground,
	isLockProtected,
}: CanEditOrDeleteViewParams): boolean => isViewPage && !isExploreSubview && !isPlayground && !isLockProtected;

const useCanEditOrDeleteCurrentView = (permission: ResourcePermission): boolean => {
	const permissions = usePermissions();
	const currentView = useCurrentView();
	const isViewPage = useSelector(selectIsViewPage);
	const isExploreSubview = useSelector(selectIsExploreSubview);
	const isPlayground = useSelector(selectPlayground);
	const isLockProtected = useIsCurrentViewLockProtected();

	return currentView
		.transform(({ name: viewName }) => {
			if (viewName == null || !permissions?.hasResourcePermission(viewName, permission)) {
				return false;
			}

			return canEditOrDeleteView({ isViewPage, isExploreSubview, isPlayground, isLockProtected });
		})
		.getOrElse(false);
};

export const useCanEditCurrentView = (): boolean => useCanEditOrDeleteCurrentView(Permission.UpdateViews);

export const useCanDeleteCurrentView = (): boolean => useCanEditOrDeleteCurrentView(Permission.DeleteViews);
